import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ChatIcon from '@mui/icons-material/Chat';
import HandymanIcon from '@mui/icons-material/Handyman';
import SignpostIcon from '@mui/icons-material/Signpost';
import WatchLaterIcon from '@mui/icons-material/WatchLater';
import { Stack, Typography } from '@mui/material';
import { FunnelFunctions, NodeVariants } from 'features/co-pilot/co-pilot.types';
import _ from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Node, useReactFlow } from 'reactflow';
import { Shadows } from 'theme';
import { makeStyles } from 'tss-react/mui';
import { splitTimeInSeconds } from 'utils/flowNode.utils';

interface Props {
  node: Node;
}

const SimpleNodeItem: React.FC<Props> = ({ node }) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();

  const { getEdges, getNodes } = useReactFlow();

  const { type, data } = node;
  const { functionName } = data;

  /**
   * Node data
   */

  const getPrimaryNodeIcon = () => {
    switch (type) {
      case NodeVariants.CONDITIONAL_NODE:
      case NodeVariants.CONDITION_WITH_DURATION_NODE:
        return <SignpostIcon className={classes.nodeHeadIcon} />;
      case NodeVariants.FUNCTION_NODE:
        return <ChatIcon className={classes.nodeHeadIcon} />;
      case NodeVariants.WAIT_NODE:
        return <WatchLaterIcon className={classes.nodeHeadIcon} />;
      case NodeVariants.UTILITY_NODE:
        return <HandymanIcon className={classes.nodeHeadIcon} />;
      default:
        return <ChatIcon className={classes.nodeHeadIcon} />;
    }
  };

  const getWaitingTime = () => {
    const edges = getEdges();
    const existingEdge = edges.find((edge) => edge.source === node.id);

    if (!_.isNil(existingEdge)) {
      const nodes = getNodes();
      const targetNode = nodes.find((node) => node.id === existingEdge.target);

      if (!_.isNil(targetNode)) {
        const { waitTillNext } = targetNode.data;
        const customWaitTime = splitTimeInSeconds(waitTillNext);

        if (!_.isNil(waitTillNext)) {
          let waitTime = '';

          if (customWaitTime.days) {
            waitTime += `${customWaitTime.days} d`;
          }

          if (customWaitTime.hours) {
            if (customWaitTime.days) {
              waitTime += ' ';
            }
            waitTime += `${customWaitTime.hours} h`;
          }

          if (customWaitTime.minutes) {
            if (customWaitTime.days || customWaitTime.hours) {
              waitTime += ' ';
            }
            waitTime += `${customWaitTime.minutes} min`;
          }

          return waitTime;
        }

        return undefined;
      }
    }

    return undefined;
  };

  const getFunctionName = () => {
    switch (functionName) {
      case FunnelFunctions.CHECK_IF_CONNECTED:
        return t('connectedOnLinkedIn');
      case FunnelFunctions.CHECK_IF_CONNECTED_UNTIL:
        return t('contactRequestAccepted');
      case FunnelFunctions.CHECK_IF_GENERATED_EMAIL_ADDRESS:
        return t('hasEmailAddress');
      case FunnelFunctions.CHECK_IS_POLITE:
        return t('politeSpeechQuestion', { defaultValue: t('politeSpeech') + '?' });
      case FunnelFunctions.WAIT_STEP:
        return t('waitingTime');
      default:
        return t(functionName);
    }
  };

  return (
    <>
      <Stack className={cx(classes.nodeItem)} direction={'row'} alignItems={'center'} justifyContent={'space-between'} gap={2}>
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          {getPrimaryNodeIcon()}
          <Typography className={classes.nodeHeadText}>{functionName ? getFunctionName() : t('noActionSelected')}</Typography>
        </Stack>
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          {getWaitingTime() ? (
            <Stack direction={'row'} alignItems={'center'} gap={2}>
              <AccessTimeIcon className={classes.regularIcon} />
              <Typography className={classes.nodeHeadText}>{t('timeUntilNextStep', { time: getWaitingTime() })}</Typography>
            </Stack>
          ) : null}
        </Stack>
      </Stack>
    </>
  );
};

const useStyles = makeStyles()((theme) => ({
  nodeItem: {
    fontSize: theme.typography.pxToRem(14),
    color: theme.palette.greys.medium,
    background: '#FFF',
    padding: '8px 16px',
    borderRadius: '8px',
    width: '100%',
    boxShadow: Shadows.GREY_SHADOW_LIGHTER,
  },
  nodeHeadIcon: {
    fontSize: theme.typography.pxToRem(20),
  },
  nodeHeadText: {
    fontSize: theme.typography.pxToRem(13),
  },
  regularIcon: {
    fontSize: theme.typography.pxToRem(20),
  },
}));

export default SimpleNodeItem;
